import { useEffect, useState } from "react";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const isSunday = () => new Date().getDay() === 0;

/**
 * "This Week" digest. Auto-loads on Sundays. Manual button any other day.
 */
export function WeeklyDigest() {
  const { user } = useAuth();
  const [open, setOpen] = useState(isSunday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/journal/weekly-digest");
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !data && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) {
    return (
      <div className="mt-6">
        <button
          data-testid="weekly-digest-open"
          onClick={() => setOpen(true)}
          className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#CFA66A] hover:text-[#EBE5D9] transition-colors"
        >
          ▸ This week&apos;s review
        </button>
      </div>
    );
  }

  return (
    <section
      data-testid="weekly-digest"
      className="mt-6 relative ai-glow border border-[#2E2A25] bg-[#0F0E0C] p-5 rounded-sm slide-up"
    >
      <div className="relative z-10 flex items-baseline justify-between mb-3 gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">— This week</p>
          <h2 className="font-head text-lg text-[#EBE5D9] mt-1">
            {data ? `${data.week_start} → ${data.week_end}` : "Weekly digest"}
          </h2>
        </div>
        <button
          data-testid="weekly-digest-close"
          onClick={() => setOpen(false)}
          className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E] hover:text-[#EBE5D9] transition-colors"
        >
          hide
        </button>
      </div>

      {loading && (
        <p className="relative z-10 font-mono text-xs text-[#A39A8E]">Compiling your week…</p>
      )}
      {error && <p className="relative z-10 font-mono text-xs text-[#B56258]">{error}</p>}
      {data && !loading && (
        <div className="relative z-10 space-y-3">
          {data.trade_count === 0 ? (
            <p className="text-sm text-[#EBE5D9] italic">
              {data.coaching_line || "No trades this week — reset and re-engage on Monday."}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Days" value={`${data.days_journaled}/7`} testid="digest-days" />
                <Stat label="Trades" value={data.trade_count} testid="digest-trades" />
                <Stat
                  label="Week P&L"
                  value={formatMoney(data.total_pnl, user?.currency)}
                  color={data.total_pnl > 0 ? "text-[#7A9E80]" : data.total_pnl < 0 ? "text-[#B56258]" : "text-[#EBE5D9]"}
                  testid="digest-pnl"
                />
                <Stat
                  label="Top pair"
                  value={data.top_pair ? `${data.top_pair.pair} ×${data.top_pair.count}` : "—"}
                  testid="digest-top-pair"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TradeChip label="Biggest win" trade={data.biggest_win} currency={user?.currency} tone="win" testid="digest-biggest-win" />
                <TradeChip label="Worst loss" trade={data.worst_loss} currency={user?.currency} tone="loss" testid="digest-worst-loss" />
              </div>

              {data.top_emotions?.length > 0 && (
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">Recurring emotions</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {data.top_emotions.map((e) => (
                      <span key={e.emotion} data-testid={`digest-emotion-${e.emotion}`} className="font-mono text-[11px] px-2 py-0.5 border border-[#2E2A25] rounded-sm text-[#EBE5D9]">
                        {e.emotion} <span className="text-[#A39A8E]">×{e.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {data.coaching_line && (
                <div data-testid="digest-coaching" className="mt-2 pt-3 border-t border-[#2E2A25]">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-[#CFA66A]">Coach&apos;s note</p>
                  <p className="text-sm text-[#EBE5D9] italic mt-1 leading-relaxed">{data.coaching_line}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, color = "text-[#EBE5D9]", testid }) {
  return (
    <div className="border border-[#2E2A25] bg-[#1A1815] p-2.5 rounded-sm">
      <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">{label}</p>
      <p data-testid={testid} className={`font-mono text-base mt-0.5 ${color}`}>{value}</p>
    </div>
  );
}

function TradeChip({ label, trade, currency, tone, testid }) {
  const color = tone === "win" ? "text-[#7A9E80]" : "text-[#B56258]";
  return (
    <div className="border border-[#2E2A25] bg-[#1A1815] p-2.5 rounded-sm">
      <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">{label}</p>
      {trade ? (
        <p data-testid={testid} className={`font-mono text-sm mt-0.5 ${color}`}>
          {trade.pair} {(trade.direction || "").toUpperCase()} · {formatMoney(trade.pnl, currency)}
        </p>
      ) : (
        <p className="font-mono text-sm mt-0.5 text-[#A39A8E]">—</p>
      )}
    </div>
  );
}
